// ProcessTipReturnModal — Phase 6.
//
// Same dual-submit shape as ProcessExchangeModal but no replacement
// unit picker (the same skip is taken for tipping and returned).
//
//   "Process Now" → POST /skip/jobs/:enc_id/actions/process_tip_return
//                  with { notes, process_now: true }.
//                  Spawns the TNR- child job + completes the parent.
//
//   "Add to Enquiries" → no server call; leaves the sub-flag queued.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './ProcessActionModal.module.css';

export interface ProcessTipReturnModalProps {
  isOpen: boolean;
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ProcessTipReturnModal({
  isOpen,
  jobEncId,
  jobNumber,
  customerName,
  onClose,
  onSuccess,
}: ProcessTipReturnModalProps): JSX.Element | null {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function processNow() {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post(
        endpoints.skipJobs.action(jobEncId, 'actions/process_tip_return'),
        { notes: notes.trim() || undefined, process_now: true },
      );
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(jobEncId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipInventory.all });
      onSuccess?.();
      onClose();
      setNotes('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  /** Client-only dismissal — see ProcessExchangeModal for the rationale. */
  function addToEnquiries() {
    setError(null);
    onClose();
  }

  return (
    <Modal
      open={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title="Process Tip & Return"
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={addToEnquiries}
            disabled={submitting}
            aria-label="Add to Enquiries: leave queued for later processing"
          >
            Add to Enquiries
          </Button>
          <Button
            variant="primary"
            onClick={() => void processNow()}
            loading={submitting}
          >
            Process Now
          </Button>
        </div>
      }
    >
      <p className={styles.body}>
        The existing skip on <strong>{jobNumber}</strong> ({customerName}) will
        be taken for tipping and returned to site.
      </p>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="tip-return-notes">
          Notes (optional)
        </label>
        <textarea
          id="tip-return-notes"
          className={styles.textarea}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      <p className={styles.note}>
        "Add to Enquiries" leaves the request queued; the job stays on
        the active-jobs page until you Process Now.
      </p>
    </Modal>
  );
}
